import { useRef, useCallback } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Switch,
  VStack,
  Text,
  IconButton,
  Icon,
  chakra,
  Tooltip,
} from '@chakra-ui/react';
import { Reorder, useDragControls } from 'framer-motion';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Trash, Plus, GripVerticalIcon } from 'lucide-react';

import { UnitAssessmentFormFieldType } from '@duke-hq/svc-api-db';
import { shortUuid7 } from '@duke-hq/id';

import type { FormFieldOptionType } from '../FormSlide/types';

const List = chakra(Reorder.Group<FormFieldOptionType>);
const ListItem = chakra(Reorder.Item);

type FormFieldOptionItemProps = {
  option: FormFieldOptionType;
  index: number;
  onDelete: () => void;
  onOptionChange: (
    index: number,
    field: keyof FormFieldOptionType,
    value: string | boolean,
  ) => void;
  lastOptionRef?: React.RefObject<HTMLInputElement | null>;
  isLast: boolean;
};

function FormFieldOptionItem({
  option,
  index,
  onDelete,
  onOptionChange,
  lastOptionRef,
  isLast,
}: FormFieldOptionItemProps) {
  const itemDragControls = useDragControls();
  const { position, isCorrect, label } = option;

  return (
    <ListItem
      value={option}
      dragListener={false}
      dragControls={itemDragControls}
      w="full"
      borderWidth="1px"
      borderRadius="md"
      p={3}
      bg="bg.surface"
    >
      <HStack spacing={2} alignItems="center" justifyContent="space-between" mb={2}>
        <HStack spacing={2}>
          <Text>
            <Trans>Option</Trans> {position}
          </Text>
          <FormLabel
            fontSize="sm"
            whiteSpace="nowrap"
            htmlFor={`correct-response-${index}`}
            m="0"
            opacity={0.4}
          >
            <Trans>Correct response</Trans>
          </FormLabel>
          <Switch
            id={`correct-response-${index}`}
            isChecked={isCorrect}
            onChange={(e) => onOptionChange(index, 'isCorrect', e.target.checked)}
            size="sm"
          />
        </HStack>

        <Tooltip label={t`Delete option`} placement="top">
          <IconButton
            aria-label={t`Delete option`}
            icon={<Icon as={Trash} boxSize="16px" />}
            size="sm"
            variant="ghost"
            colorScheme="red"
            onClick={onDelete}
          />
        </Tooltip>
      </HStack>
      <HStack>
        <Input
          ref={isLast ? lastOptionRef : undefined}
          value={label}
          onChange={(e) => onOptionChange(index, 'label', e.target.value)}
          placeholder={t`Enter option text`}
          required
        />
        <Tooltip label={t`Drag to reorder`} placement="top">
          <IconButton
            icon={<Icon as={GripVerticalIcon} boxSize="16px" />}
            aria-label={t`Reorder option`}
            size="sm"
            variant="ghost"
            cursor="grab"
            onPointerDown={(e) => {
              itemDragControls.start(e);
            }}
          />
        </Tooltip>
      </HStack>
    </ListItem>
  );
}

type FormFieldEditorOptionsProps = {
  options: FormFieldOptionType[];
  setOptions: (callback: (prev: FormFieldOptionType[]) => FormFieldOptionType[]) => void;
  fieldType: UnitAssessmentFormFieldType;
};

export function FormFieldEditorOptions({
  options,
  setOptions,
  fieldType,
}: FormFieldEditorOptionsProps) {
  const lastOptionRef = useRef<HTMLInputElement>(null);

  const handleOptionChange = useCallback(
    (index: number, field: keyof FormFieldOptionType, value: string | boolean) => {
      setOptions((prev) => {
        const newOptions = prev.map((option) => ({ ...option }));
        const existingOption = newOptions[index];

        if (existingOption !== undefined) {
          // Only allow one correct option
          if (field === 'isCorrect' && value === true) {
            newOptions.forEach((option) => (option.isCorrect = false));
          }

          newOptions[index] = { ...existingOption, [field]: value };
        }

        return newOptions;
      });
    },
    [setOptions],
  );

  const handleDeleteOption = useCallback(
    (index: number) => {
      setOptions((prev) => {
        const filteredOptions = prev.filter((_, i) => i !== index);
        // Reset positions
        return filteredOptions.map((option, i) => ({ ...option, position: i + 1 }));
      });
    },
    [setOptions],
  );

  const handleAddOption = useCallback(() => {
    setOptions((prev) => [
      ...prev,
      {
        id: '',
        label: '',
        position: prev.length + 1,
        isCorrect: options.length === 0,
        _draftId: shortUuid7(),
      },
    ]);

    // Focus on the new option
    queueMicrotask(() => lastOptionRef.current?.focus());
  }, [setOptions, options]);

  const handleReorder = useCallback(
    (newOrder: FormFieldOptionType[]) => {
      setOptions(() => newOrder.map((option, i) => ({ ...option, position: i + 1 })));
    },
    [setOptions],
  );

  const optionTypeLabel = fieldType === UnitAssessmentFormFieldType.RADIO ? 'Radio' : 'Select';

  return (
    <FormControl mb={4}>
      <FormLabel>
        <Trans>Response Options</Trans>
      </FormLabel>

      <List values={options} onReorder={handleReorder} listStyleType="none" w="full">
        <VStack spacing={2} align="stretch" w="full">
          {options.map((option, index) => (
            <FormFieldOptionItem
              key={option.id !== '' ? option.id : option._draftId}
              option={option}
              index={index}
              onDelete={() => handleDeleteOption(index)}
              onOptionChange={handleOptionChange}
              lastOptionRef={lastOptionRef}
              isLast={index === options.length - 1}
            />
          ))}
        </VStack>
      </List>

      <Button
        leftIcon={<Plus size={16} />}
        variant="ghost"
        borderWidth={2}
        borderColor="colors.borderEmphasis"
        borderStyle="dashed"
        size="md"
        py="22px"
        w="full"
        onClick={handleAddOption}
        mt={2}
      >
        <Trans>Add {optionTypeLabel} Option</Trans>
      </Button>
    </FormControl>
  );
}
